import { useState, useContext } from "react";
import { useHistory } from "react-router-dom";

import { Box, Typography, Grid } from "@mui/material";

import { nameToFirstName } from "@aclymatepackages/converters";

import ResendVerificationEmailButton from "./ResendVerificationEmailButton";
import AccountCreationInput from "./AccountCreationInput";
import MergeConfirmationStep from "./MergeConfirmationStep";

import Link from "../atoms/mui/Link";

import { AccountAccessContext } from "../../helpers/contexts/accountAccess";
import {
  onSignInWithEmailAndPassword,
  onLoginSetSessionAndTracking,
  useReferralCookie
} from "../../helpers/components/accountAccess";
import {
  fetchOurApi,
  fetchUserWithEmail,
  newAuthUser
} from "../../helpers/utils/apiCalls";
import { useAuth } from "../../helpers/firebase";
import { analyticsTrack, triggerGoogleEvent } from "../../helpers/analytics";

const doesUserAlreadyExist = async (email) => {
  const existingUserData = await fetchUserWithEmail(email);
  return !!existingUserData;
};

const createNewAuthUser = async ({ user, callback }) => {
  const { email, displayName } = user;
  analyticsTrack("User Created", { email, displayName });
  return await newAuthUser(user, callback);
};

const AccountCreationForm = ({
  isNewCompanyAdmin,
  setError,
  companyId,
  stripeCustomerId,
  referralPartner,
  setVerificationEmailSent,
  initialUserData,
  setEmail,
  setDisplayName
}) => {
  const [currentUser] = useAuth();

  const { email: storedEmail, token: inviteToken } = isNewCompanyAdmin || {};

  const history = useHistory();

  const { referralPartner: partnerCookie } = useReferralCookie();
  const { setAccountAccessError } = useContext(AccountAccessContext) || {};
  const setErrorMsg = setAccountAccessError || setError;

  const [userLoading, setUserLoading] = useState(false);
  const [pendingMerge, setPendingMerge] = useState(null);

  const { email: initialEmail = "" } = initialUserData || {};

  const confirmNewCompanyAdmin = async ({
    loginFunction,
    user: passedUser
  }) => {
    const fetchAdminUser = async () => {
      if (loginFunction) {
        return await loginFunction();
      }

      return currentUser;
    };

    const user = passedUser || (await fetchAdminUser());
    const { uid, email, displayName } = user || {};

    const loginNewAdmin = async (response) => {
      if (response?.error) {
        setUserLoading(false);
        return showAccountAccessSnackbar(
          "Your invitation link may be invalid or expired. Please contact your account administrator."
        );
      }
      await user.getIdToken(true);
      onLoginSetSessionAndTracking(user, companyId);
      history.push("/platform/company/dashboard");
      return window.location.reload();
    };

    return await fetchOurApi({
      path: `/users/confirm`,
      method: "POST",
      data: {
        companyId,
        uid,
        email,
        name: displayName,
        token: inviteToken
      },
      callback: loginNewAdmin
    });
  };

  const showAccountAccessSnackbar = (message) => {
    setUserLoading(false);
    return setErrorMsg(message);
  };

  const processNewAdminUserInvite = async ({
    user,
    inputData,
    loginFunction
  }) => {
    const { email, displayName, password } = inputData || user;

    if (
      isNewCompanyAdmin &&
      email?.toLowerCase() !== storedEmail?.toLowerCase()
    ) {
      return showAccountAccessSnackbar(
        "The email you've input doesn't match the email of your invitation"
      );
    }

    setUserLoading(true);

    const newUserProps = {
      email: email || initialEmail,
      displayName,
      password
    };

    if (loginFunction) {
      return await createNewAuthUser({
        user: newUserProps,
        callback: () => confirmNewCompanyAdmin({ loginFunction })
      });
    }

    return await confirmNewCompanyAdmin({ user });
  };

  const preCompanyAccountCreationChecks = async ({
    inputData,
    loginFunction,
    user
  }) => {
    const {
      email,
      displayName,
      uid: existingUid,
      password
    } = inputData || user;

    const joinOrphanCompany = async ({
      mergeDecision,
      confirmedUid,
      confirmedMatchedCompanyName,
      confirmedRelationshipCount
    } = {}) => {
      const callLoginFunction = async () => {
        if (loginFunction) {
          return await loginFunction(inputData);
        }

        return;
      };

      const onSuccessfulJoin = async (response) => {
        const {
          requiresConfirmation,
          uid: createdUid,
          matchedCompanyName,
          relationshipCount
        } = response || {};

        if (requiresConfirmation) {
          setUserLoading(false);
          return setPendingMerge({
            matchedCompanyName,
            relationshipCount,
            resolve: (decision) =>
              joinOrphanCompany({
                mergeDecision: decision,
                confirmedUid: createdUid,
                confirmedMatchedCompanyName: matchedCompanyName,
                confirmedRelationshipCount: relationshipCount
              })
          });
        }

        setPendingMerge(null);
        await callLoginFunction();

        return setVerificationEmailSent(true);
      };

      return await fetchOurApi({
        path: `/onboarding/join-orphan-company`,
        method: "POST",
        data: {
          companyId,
          uid: confirmedUid || existingUid,
          email,
          displayName,
          password: confirmedUid ? undefined : password,
          mergeDecision,
          confirmedMatchedCompanyName,
          confirmedRelationshipCount
        },
        callback: onSuccessfulJoin,
        setError: () =>
          showAccountAccessSnackbar("An error occurred joining your company.")
      });
    };

    if (isNewCompanyAdmin) {
      await processNewAdminUserInvite({
        loginFunction,
        inputData,
        user
      });
      return false;
    }

    if (companyId) {
      await joinOrphanCompany();

      return false;
    }

    return true;
  };

  const passwordEmailSignIn = async (inputData, otherProps = {}) =>
    await onSignInWithEmailAndPassword({
      setErrorMsg: showAccountAccessSnackbar,
      callback: (user) => user,
      ...inputData,
      ...otherProps
    });

  const companyAccountCallback = async ({
    email,
    mergeDecision,
    matchedCompanyId
  }) => {
    const {
      error,
      message,
      requiresConfirmation,
      matchedCompanyId: newMatchedCompanyId,
      matchedCompanyName,
      relationshipCount
    } = await fetchOurApi({
      path: "/onboarding/company-account-creation",
      method: "POST",
      data: {
        email,
        referralPartner: partnerCookie || referralPartner,
        mergeDecision,
        matchedCompanyId,
        stripeCustomerId
      },
      callback: (res) => res
    });

    if (requiresConfirmation) {
      setUserLoading(false);
      return setPendingMerge({
        matchedCompanyName,
        relationshipCount,
        resolve: (decision) =>
          companyAccountCallback({
            email,
            mergeDecision: decision,
            matchedCompanyId: newMatchedCompanyId
          })
      });
    }

    if (error) {
      return showAccountAccessSnackbar(message);
    }

    setPendingMerge(null);
    triggerGoogleEvent("account_created");
    analyticsTrack("Company Created");
    return setVerificationEmailSent(true);
  };

  const completePasswordEmailAccountCreation = async (inputData) => {
    const { email, displayName } = inputData;

    setUserLoading(true);
    setEmail(email);
    setDisplayName(displayName);

    const userAlreadyExists = await doesUserAlreadyExist(email);

    if (userAlreadyExists) {
      return showAccountAccessSnackbar(
        "A user with this email already exists. Try logging in instead."
      );
    }

    const doesPassPreAccountCreationChecks =
      await preCompanyAccountCreationChecks({
        inputData,
        loginFunction: () => passwordEmailSignIn(inputData)
      });

    if (!doesPassPreAccountCreationChecks) {
      return;
    }

    await createNewAuthUser({ user: inputData });

    return await passwordEmailSignIn(inputData, {
      callback: () => companyAccountCallback(inputData)
    });
  };

  const completeOauthAccountCreation = async (user) => {
    setUserLoading(true);
    const doesPassPreAccountCreationChecks =
      await preCompanyAccountCreationChecks({ user });

    if (!doesPassPreAccountCreationChecks) {
      return;
    }

    return await companyAccountCallback(user);
  };

  if (pendingMerge) {
    const { matchedCompanyName, relationshipCount, resolve } = pendingMerge;

    return (
      <MergeConfirmationStep
        matchedCompanyName={matchedCompanyName}
        relationshipCount={relationshipCount}
        onDecision={resolve}
      />
    );
  }

  return (
    <AccountCreationInput
      isNewCompanyAdmin={isNewCompanyAdmin}
      setErrorMsg={setErrorMsg}
      completeOauthAccountCreation={completeOauthAccountCreation}
      initialUserData={initialUserData}
      userLoading={userLoading}
      completePasswordEmailAccountCreation={
        completePasswordEmailAccountCreation
      }
    />
  );
};

const EmailConfirmationBlock = ({ email, displayName = "" }) => (
  <Grid item>
    <Box py={4}>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Typography variant="h4" color="textPrimary">
            Hi {nameToFirstName(displayName)}, Welcome to Aclymate.
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            We just sent you an email with a link to the next steps. Click the
            button below if you didn&apos;t receive it.
          </Typography>
        </Grid>
        <Grid item container justifyContent="center">
          <Grid item>
            <ResendVerificationEmailButton email={email} />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  </Grid>
);

const AccountCreationFlow = ({ isNewCompanyAdmin, ...otherProps }) => {
  const { email: storedEmail } = isNewCompanyAdmin || {};

  const initialUserData = storedEmail ? { email: storedEmail } : {};
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");

  return (
    <>
      {verificationEmailSent ? (
        <EmailConfirmationBlock email={email} displayName={displayName} />
      ) : (
        <AccountCreationForm
          initialUserData={initialUserData}
          isNewCompanyAdmin={isNewCompanyAdmin}
          setVerificationEmailSent={setVerificationEmailSent}
          setEmail={setEmail}
          setDisplayName={setDisplayName}
          {...otherProps}
        />
      )}
      <Grid item container justifyContent="center">
        <Grid item>
          <Typography variant="body1" display="inline">
            Already have an account? <Link href="/">Login instead</Link>
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};
export default AccountCreationFlow;
